import axiosInstance from '../utils/axios';
import { message } from 'antd';

export interface DoctorQueryParams {
  page?: number;
  pageSize?: number;
  name?: string;
  hospitalName?: string;
  departmentName?: string;
  gender?: string;
  title?: string;
  phoneBindingStatus?: boolean;
  project_id?: number;
}

export interface Doctor {
  id: number;
  hospitalProvince: string;
  hospitalCity: string;
  hospitalName: string;
  departmentName: string;
  name: string;
  title: string;
  gender: string;
  idCard: string;
  phone: string;
  phoneBindingStatus: boolean;
  bankName?: string;
  bankAccount?: string;
  wechatQrcode?: string;
  idCardFront?: string;
  idCardBack?: string;
  bankCardFront?: string;
  bankCardBack?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface StatItem {
  name: string;
  count: number;
  percentage: string;
}

export interface DoctorStats {
  total: number;
  gender: Array<{ name: string; count: number }>;
  title: Array<{ name: string; count: number }>;
  phoneBinding: Array<{ name: string; count: number }>;
  province: Array<{ name: string; count: number }>;
}

export interface DoctorQRCode {
  doctor_id: number;
  doctor_name: string;
  qr_code_url: string;
  scene_str: string;
  ticket?: string;
  is_auto_generated?: boolean;
}

export interface DoctorQRScanRecord {
  id: number;
  doctor_id: number;
  openid: string;
  nickname?: string;
  avatar?: string;
  sex?: number;
  city?: string;
  province?: string;
  country?: string;
  subscribe_time: string;
  is_unsubscribed: boolean;
  unsubscribe_time?: string;
}

class DoctorService {
  async getDoctors(params: DoctorQueryParams) {
    try {
      const response = await axiosInstance.get('/doctors', { params });
      return response.data;
    } catch (error) {
      console.error('获取医生列表失败:', error);
      throw error;
    }
  }

  async getDoctor(id: number) {
    try {
      const response = await axiosInstance.get(`/doctors/${id}`);
      return response.data;
    } catch (error) {
      console.error('获取医生详情失败:', error);
      throw error;
    }
  }

  async createDoctor(data: Omit<Doctor, 'id'>) {
    try {
      const response = await axiosInstance.post('/doctors', data);
      return response.data;
    } catch (error) {
      console.error('创建医生失败:', error);
      throw error;
    }
  }

  async updateDoctor(id: number, data: Partial<Doctor>) {
    try {
      const response = await axiosInstance.put(`/doctors/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('更新医生失败:', error);
      throw error;
    }
  }

  async deleteDoctor(id: number) {
    try {
      const response = await axiosInstance.delete(`/doctors/${id}`);
      return response.data;
    } catch (error) {
      console.error('删除医生失败:', error);
      throw error;
    }
  }

  async getStats() {
    try {
      const response = await axiosInstance.get('/doctors/stats');
      return response.data;
    } catch (error) {
      console.error('获取医生统计信息失败:', error);
      throw error;
    }
  }

  // 二维码相关方法
  async generateDoctorQRCode(doctorId: number): Promise<DoctorQRCode> {
    try {
      const response = await axiosInstance.post(`/doctor-qr-codes/generate/${doctorId}`);
      return response.data.data;
    } catch (error) {
      console.error('生成医生二维码失败:', error);
      throw error;
    }
  }

  async getDoctorQRCode(doctorId: number): Promise<DoctorQRCode> {
    try {
      const response = await axiosInstance.get(`/doctor-qr-codes/get/${doctorId}`);
      return response.data.data;
    } catch (error) {
      console.error('获取医生二维码失败:', error);
      throw error;
    }
  }

  async batchGenerateQRCodes(doctorIds: number[]) {
    try {
      const response = await axiosInstance.post('/doctor-qr-codes/batch-generate', { doctorIds });
      return response.data.data;
    } catch (error) {
      console.error('批量生成二维码失败:', error);
      throw error;
    }
  }

  async getDoctorQRScanRecords(doctorId: number, page = 1, limit = 10) {
    try {
      const response = await axiosInstance.get(`/doctor-qr-codes/scan-records/${doctorId}`, {
        params: { page, limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('获取医生二维码扫描记录失败:', error);
      throw error;
    }
  }
}

export const doctorService = new DoctorService(); 